import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Layout,
	Card,
	Tabs,
	Table,
	Button,
	Space,
	Modal,
	Form,
	Input,
	DatePicker,
	message,
	ConfigProvider,
	Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";

import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import AccessedCarNavigation from "../AccessedCarNavigation";
import useIsMobile from "../../../../hooks/useIsMobile";
import PageHeader from "../../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;

const API_LIST = "/utils/rental/car/insurances/";

const VehicleInsuranceList = () => {
	const isMobile = useIsMobile();
	const [activeTab, setActiveTab] = useState("pending");
	const [list, setList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [searchText, setSearchText] = useState("");
	const [form] = Form.useForm();

	const isRenewedTab = activeTab === "renewed";

	const filteredList = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		if (!keyword) {
			return list;
		}
		return list.filter((row) =>
			String(row.vehicle || "")
				.toLowerCase()
				.includes(keyword),
		);
	}, [list, searchText]);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await useAxios.get(API_LIST, {
				params: {
					renewed: isRenewedTab ? "true" : "false",
				},
			});
			if (res.status === 200) {
				setList(res.data.vehicle_insurances || []);
			}
		} catch {
			message.error("차량 보험 내역을 불러오지 못했습니다.");
			setList([]);
		} finally {
			setLoading(false);
		}
	}, [isRenewedTab]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditing(record);
		form.setFieldsValue({
			vehicle: record.vehicle || "",
			expiry_date: record.expiry_date
				? dayjs(record.expiry_date)
				: null,
			recorded_date: record.recorded_date
				? dayjs(record.recorded_date)
				: null,
			insurer_contact: record.insurer_contact || "",
			renewal_date: record.renewal_date
				? dayjs(record.renewal_date)
				: null,
		});
		setModalOpen(true);
	};

	const handleSubmit = async (values) => {
		const payload = {
			vehicle: values.vehicle?.trim() || "",
			expiry_date: values.expiry_date.format("YYYY-MM-DD"),
			recorded_date: values.recorded_date
				? values.recorded_date.format("YYYY-MM-DD")
				: null,
			insurer_contact: values.insurer_contact?.trim() || "",
			renewal_date: values.renewal_date
				? values.renewal_date.format("YYYY-MM-DD")
				: null,
		};
		try {
			setLoading(true);
			if (editing) {
				const res = await useAxios.put(
					`${API_LIST}${editing.id}/`,
					payload,
				);
				if (res.status === 200) {
					message.success("보험 내역을 수정했습니다.");
					setModalOpen(false);
					setEditing(null);
					form.resetFields();
					loadData();
				}
			} else {
				const res = await useAxios.post(API_LIST, payload);
				if (res.status === 201) {
					message.success("보험 내역을 등록했습니다.");
					setModalOpen(false);
					form.resetFields();
					loadData();
				}
			}
		} catch {
			message.error(
				editing
					? "보험 내역 수정에 실패했습니다."
					: "보험 내역 등록에 실패했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		try {
			setLoading(true);
			const res = await useAxios.delete(`${API_LIST}${id}/`);
			if (res.status === 200) {
				message.success("보험 내역을 삭제했습니다.");
				loadData();
			}
		} catch {
			message.error("보험 내역 삭제에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "차량",
			dataIndex: "vehicle",
			key: "vehicle",
			render: (v) => v || "-",
		},
		{
			title: "만기일자",
			dataIndex: "expiry_date",
			key: "expiry_date",
			render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
		},
		{
			title: "녹취일",
			dataIndex: "recorded_date",
			key: "recorded_date",
			render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
		},
		{
			title: "보험사 담당자",
			dataIndex: "insurer_contact",
			key: "insurer_contact",
			render: (v) => v || "-",
		},
		{
			title: "갱신일",
			dataIndex: "renewal_date",
			key: "renewal_date",
			render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
		},
		{
			title: "관리",
			key: "actions",
			render: (_, record) => (
				<Space>
					<Button
						type="primary"
						size="small"
						icon={<EditOutlined />}
						onClick={() => openEdit(record)}>
						수정
					</Button>
					<Popconfirm
						title="정말 삭제하시겠습니까?"
						onConfirm={() => handleDelete(record.id)}
						okText="삭제"
						cancelText="취소"
						okButtonProps={{ danger: true }}>
						<Button
							type="primary"
							danger
							size="small"
							icon={<DeleteOutlined />}>
							삭제
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="차량 보험 관리" />
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader
								title="차량 보험 관리"
								actions={
									<>
										<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={openCreate}>
									보험 내역 추가
								</Button>
									</>
								}
							/>
							<Card
								className="surface-card"
								style={{
									width: "100%",
									minWidth: 0,
								}}>
								<Tabs
									activeKey={activeTab}
									onChange={setActiveTab}
									items={[
										{
											key: "pending",
											label: "미도래",
										},
										{
											key: "renewed",
											label: "갱신완료",
										},
									]}
								/>
								<Input.Search
									allowClear
									placeholder="차량번호 검색"
									style={{
										maxWidth: 280,
										marginBottom: 16,
									}}
									value={searchText}
									onChange={(e) =>
										setSearchText(
											e.target.value,
										)
									}
								/>
								<Table
									rowKey="id"
									loading={loading}
									columns={columns}
									dataSource={filteredList}
									pagination={{
										pageSize: 20,
									}}
									scroll={{ x: "max-content" }}
									sticky={{ offsetHeader: 64 }}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title={editing ? "보험 내역 수정" : "보험 내역 추가"}
				open={modalOpen}
				onCancel={() => {
					setModalOpen(false);
					setEditing(null);
					form.resetFields();
				}}
				footer={null}
				destroyOnClose
				width={520}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}>
					<Form.Item
						name="vehicle"
						label="차량"
						rules={[
							{
								required: true,
								message: "차량을 입력하세요.",
							},
						]}>
						<Input placeholder="차량을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="expiry_date"
						label="만기일자"
						rules={[
							{
								required: true,
								message: "만기일자를 선택하세요.",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							format="YYYY-MM-DD"
						/>
					</Form.Item>
					<Form.Item name="recorded_date" label="녹취일">
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							format="YYYY-MM-DD"
							allowClear
						/>
					</Form.Item>
					<Form.Item
						name="insurer_contact"
						label="보험사 담당자">
						<Input placeholder="보험사 담당자 (선택)" />
					</Form.Item>
					<Form.Item name="renewal_date" label="갱신일">
						<DatePicker
							inputReadOnly={isMobile}
							style={{ width: "100%" }}
							format="YYYY-MM-DD"
							allowClear
						/>
					</Form.Item>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block>
							{editing ? "저장" : "등록"}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default VehicleInsuranceList;
